<script setup lang="ts">
import { gql } from '@apollo/client/core';
import { useLazyQuery } from '@vue/apollo-composable';
import { useConfirm } from 'primevue/useconfirm';
import { useManualOutstandingMutations } from '@/composables/mutations/tertiary/useManualOutstandingMutations';
import { useStudents } from '@/composables/queries/useStudent';
import { useCourses } from '@/composables/queries/useCourses';
import { EnrollmentStatusEnum } from '@/types/composition-functions';
import BulkOutstandingByStudentsDialog from './BulkOutstandingByStudentsDialog.vue';
import BulkOutstandingByScopeDialog from './BulkOutstandingByScopeDialog.vue';

const confirm = useConfirm();

const {
  addManualOutstanding, adding,
  deleteManualOutstanding, deleting,
  bulkDeleteManualOutstanding, bulkDeleting,
  cleanupResolved, cleaningUp,
} = useManualOutstandingMutations();

const LIST_QUERY = gql`
  query ListManualOutstandingCourses($student_id: ID, $first: Int!, $page: Int) {
    listManualOutstandingCourses(student_id: $student_id, first: $first, page: $page) {
      data {
        id
        student_id
        course_id
        status
        curriculum_level
        reason
        is_resolved
        student { id first_name surname matric_no }
        course { id course_code course_title unit }
        createdAt
      }
      paginatorInfo { currentPage lastPage perPage total hasMorePages }
    }
  }
`;

const filterStudentId = ref('');
const page = ref(1);
const variables = computed(() => ({
  student_id: filterStudentId.value || undefined,
  first: 25,
  page: page.value,
}));

const { result, loading, refetch, load } = useLazyQuery(LIST_QUERY, variables);
load();

const items = computed(() => result.value?.listManualOutstandingCourses?.data ?? []);
const paginatorInfo = computed(() => result.value?.listManualOutstandingCourses?.paginatorInfo ?? null);
const selectedRows = ref<Array<Record<string, unknown>>>([]);

const hasResolved = computed(() => items.value.some((i: Record<string, unknown>) => i.is_resolved));

const showAddDialog = ref(false);
const showBulkByStudents = ref(false);
const showBulkByScope = ref(false);
const showRemoveByScope = ref(false);

// Student search for single-add dialog
const studentSearchFilters = ref({ first: 50, page: 1, filter: { search: '', status: EnrollmentStatusEnum.Enrolled } });
const { students: addStudents, loadStudents: loadAddStudents } = useStudents({
  getStudentsOptions: { autoFetch: false },
  studentFilters: studentSearchFilters as never,
});
let addStudentDebounce: ReturnType<typeof setTimeout> | null = null;
function onAddStudentFilter(event: { value: string }) {
  if (addStudentDebounce) clearTimeout(addStudentDebounce);
  addStudentDebounce = setTimeout(() => {
    studentSearchFilters.value = { first: 50, page: 1, filter: { search: event.value, status: EnrollmentStatusEnum.Enrolled } };
    loadAddStudents();
  }, 300);
}
const addStudentOptions = computed(() =>
  addStudents.data.value.map((s) => ({ label: `${s.matric_no} - ${s.surname} ${s.first_name}`, value: s.id })),
);

// Course search for single-add dialog
const { courses: addCourses, setFilter: setAddCourseFilter } = useCourses({ autoFetch: true, first: 100, page: 1 });
let addCourseDebounce: ReturnType<typeof setTimeout> | null = null;
function onAddCourseFilter(event: { originalEvent: Event; value: string }) {
  if (addCourseDebounce) clearTimeout(addCourseDebounce);
  addCourseDebounce = setTimeout(() => { setAddCourseFilter({ query: event.value }); }, 300);
}
const addCourseOptions = computed(() =>
  addCourses.value.map((c: { id: string; course_code: string; course_title: string; unit: number }) => ({
    label: `${c.course_code} — ${c.course_title} (${c.unit}u)`, value: c.id,
  })),
);

const addForm = reactive({
  student_id: '' as string,
  course_id: '' as string,
  status: 'C' as 'C' | 'E',
  curriculum_level: '',
  reason: '',
});

const statusOptions = [
  { label: 'Compulsory', value: 'C' },
  { label: 'Elective', value: 'E' },
];

function resetAddForm() {
  addForm.student_id = '';
  addForm.course_id = '';
  addForm.status = 'C';
  addForm.curriculum_level = '';
  addForm.reason = '';
}

async function handleAdd() {
  if (!addForm.student_id || !addForm.course_id) return;
  const result = await addManualOutstanding({
    student_id: addForm.student_id,
    course_id: addForm.course_id,
    status: addForm.status,
    curriculum_level: addForm.curriculum_level || undefined,
    reason: addForm.reason || undefined,
  });
  if (result?.success) {
    showAddDialog.value = false;
    resetAddForm();
    await refetch();
  }
}

async function handleDeleteRow(id: string) {
  const result = await deleteManualOutstanding(id);
  if (result?.success) await refetch();
}

function handleBulkDelete() {
  confirm.require({
    message: `Remove ${selectedRows.value.length} manual outstanding course(s)?`,
    header: 'Confirm Delete',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: async() => {
      const ids = selectedRows.value.map(r => r.id as string);
      const result = await bulkDeleteManualOutstanding(ids);
      if (result?.success) { selectedRows.value = []; await refetch(); }
    },
  });
}

function handleCleanup() {
  confirm.require({
    message: 'Remove all manual outstanding entries where the student has already passed the course?',
    header: 'Clean Up Resolved',
    icon: 'pi pi-broom',
    acceptLabel: 'Clean Up',
    accept: async() => {
      const result = await cleanupResolved();
      if (result?.success) await refetch();
    },
  });
}

async function onDialogSuccess() {
  await refetch();
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
watch(filterStudentId, () => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => { page.value = 1; refetch(); }, 400);
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="bg-primary/10 text-primary-700 p-3 rounded text-sm flex items-start gap-2">
      <i class="pi pi-info-circle mt-0.5"></i>
      <div>
        <p class="font-semibold">Manual Outstanding Courses</p>
        <p>Use this when the system can't auto-detect outstanding courses. Courses added here appear on the student's course registration page regardless of semester.</p>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <Button label="Add Single" icon="pi pi-plus" size="small" @click="showAddDialog = true" />
      <Button label="Bulk by Students" icon="pi pi-users" size="small" severity="secondary" outlined @click="showBulkByStudents = true" />
      <Button label="Assign by Scope" icon="pi pi-sitemap" size="small" severity="secondary" outlined @click="showBulkByScope = true" />
      <Button label="Remove by Scope" icon="pi pi-minus-circle" size="small" severity="warning" outlined @click="showRemoveByScope = true" />
      <Button v-if="hasResolved" label="Clean Up Resolved" icon="pi pi-broom" size="small"
        severity="warning" outlined @click="handleCleanup" :loading="cleaningUp" />
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Filter by student</label>
        <InputText v-model="filterStudentId" placeholder="Matric number..." size="small" class="w-full rounded-lg" />
      </div>
    </div>

    <div v-if="selectedRows.length > 0"
      class="bg-primary/10 border border-primary/20 rounded-lg p-3 flex items-center justify-between">
      <span class="text-sm text-primary-700 font-medium">{{ selectedRows.length }} selected</span>
      <div class="flex gap-2">
        <Button label="Delete" icon="pi pi-trash" size="small" severity="danger" outlined
          @click="handleBulkDelete" :loading="bulkDeleting" />
        <Button label="Clear" icon="pi pi-times-circle" size="small" severity="secondary" text
          @click="selectedRows = []" />
      </div>
    </div>

    <DataTable size="small" :value="items" :loading="loading" v-model:selection="selectedRows"
      dataKey="id" stripedRows tableStyle="min-width: 50rem">
      <template #empty>No manual outstanding courses found.</template>
      <Column selectionMode="multiple" headerStyle="width: 3rem" />
      <Column header="Student">
        <template #body="{ data }">
          <div>
            <div class="font-medium text-sm">{{ data.student?.surname }} {{ data.student?.first_name }}</div>
            <div class="text-xs text-gray-500">{{ data.student?.matric_no }}</div>
          </div>
        </template>
      </Column>
      <Column header="Course">
        <template #body="{ data }">
          <div>
            <div class="font-medium text-sm">{{ data.course?.course_code }}</div>
            <div class="text-xs text-gray-500">{{ data.course?.course_title }}</div>
          </div>
        </template>
      </Column>
      <Column header="Units">
        <template #body="{ data }">
          <span class="bg-gray-100 px-2 py-0.5 rounded text-xs font-bold">{{ data.course?.unit }}</span>
        </template>
      </Column>
      <Column header="Type">
        <template #body="{ data }">
          <span class="px-2 py-1 text-xs font-semibold rounded"
            :class="data.status === 'C' ? 'bg-primary/10 text-primary' : 'bg-orange-100 text-orange-700'">
            {{ data.status === 'C' ? 'Compulsory' : 'Elective' }}
          </span>
        </template>
      </Column>
      <Column header="Level">
        <template #body="{ data }">
          <span class="text-sm">{{ data.curriculum_level || '—' }}</span>
        </template>
      </Column>
      <Column header="Reason">
        <template #body="{ data }">
          <span class="text-sm text-gray-600 truncate max-w-[150px] inline-block" :title="data.reason">{{ data.reason || '—' }}</span>
        </template>
      </Column>
      <Column header="Status">
        <template #body="{ data }">
          <span v-if="data.is_resolved" class="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-700">
            Resolved
          </span>
          <span v-else class="px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-700">
            Active
          </span>
        </template>
      </Column>
      <Column header="Actions" headerStyle="width: 5rem">
        <template #body="{ data }">
          <Button icon="pi pi-trash" size="small" severity="danger" text
            @click="handleDeleteRow(data.id)" :loading="deleting" />
        </template>
      </Column>
    </DataTable>

    <!-- Add Single Dialog -->
    <Dialog v-model:visible="showAddDialog" header="Add Manual Outstanding Course" modal class="w-full md:w-5/12">
      <div class="flex flex-col gap-4 py-2">
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Student *</label>
          <Select v-model="addForm.student_id" :options="addStudentOptions" optionLabel="label" optionValue="value"
            placeholder="Search student by matric or name..." filter @filter="onAddStudentFilter" showClear class="w-full" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Course *</label>
          <Select v-model="addForm.course_id" :options="addCourseOptions" optionLabel="label" optionValue="value"
            placeholder="Search course by code or title..." filter @filter="onAddCourseFilter" showClear class="w-full" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Type *</label>
          <SelectButton v-model="addForm.status" :options="statusOptions" optionLabel="label" optionValue="value" :allowEmpty="false" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Original Level (optional)</label>
          <InputText v-model="addForm.curriculum_level" placeholder="e.g., 100, 200" class="w-full" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Reason (optional)</label>
          <Textarea v-model="addForm.reason" placeholder="Why this course is outstanding..." rows="2" class="w-full" />
        </div>
      </div>
      <template #footer>
        <Button label="Cancel" severity="secondary" text @click="showAddDialog = false" />
        <Button label="Add" icon="pi pi-check" @click="handleAdd" :loading="adding"
          :disabled="!addForm.student_id || !addForm.course_id" />
      </template>
    </Dialog>

    <!-- Bulk Dialogs -->
    <BulkOutstandingByStudentsDialog v-model:visible="showBulkByStudents" @success="onDialogSuccess" />
    <BulkOutstandingByScopeDialog v-model:visible="showBulkByScope" mode="assign" @success="onDialogSuccess" />
    <BulkOutstandingByScopeDialog v-model:visible="showRemoveByScope" mode="remove" @success="onDialogSuccess" />

    <ConfirmDialog />
  </div>
</template>
